import { useCallback, useState } from 'react';
import { useSystemLanguageValue } from 'hooks/business/system/useSystem';
import useStatus from 'hooks/useStatus';
import { overseaTranslate } from 'services/api/overseaTranslate';

import {
  useCommentRatingValue,
  useCommentsTranslationController,
} from 'components/TradeComment/useCommentsTranslationController';
import { getCommentTranslation } from 'views/Conversation/Sider/useTradeTranslate';
import { Popover, Rate } from 'antd';
import CommentPopoverContentView from 'components/TradeComment/CommentPopoverContentView';
import { DownOutlined } from '@ant-design/icons';
import { getPopupContainer } from 'components/LightConversation/utils/popover';

interface OrderCommentProps {
  platform: string;
  comments: Conversation.Trade.Comment[];
}

const CommentContent = (props: OrderCommentProps) => {
  const { comments, platform } = props;
  const systemLang = useSystemLanguageValue();

  const [localTranslations, setLocalTranslations] = useState<Record<string, string>>({});

  const { pending, error, done } = useStatus();

  const getLocalKey = useCallback(
    (commentId: string) => `${commentId}:${systemLang}`,
    [systemLang],
  );

  const getTranslationFromComment = useCallback(
    (comment: Conversation.Trade.Comment) => {
      const key = getLocalKey(comment.commentId);
      return localTranslations[key] || getCommentTranslation(comment.transBuyerComment, systemLang);
    },
    [getLocalKey, localTranslations, systemLang],
  );

  const translateMissing = useCallback(
    async ({ comment }: { comment: Conversation.Trade.Comment; commentIndex: number }) => {
      if (!comment.buyerComment) return;

      done.pending();
      try {
        const res = await overseaTranslate(
          comment.buyerComment,
          systemLang as unknown as General.Language,
          'side',
        );
        setLocalTranslations((prev) => {
          return {
            ...prev,
            [getLocalKey(comment.commentId)]: res,
          };
        });
        done();
      } catch (e) {
        done.error(e as Error);
        throw e;
      }
    },
    [done, getLocalKey, systemLang],
  );

  const {
    needTranslate,
    displayTranslation,
    translations,
    translating,
    translationError,
    toggleTranslation,
  } = useCommentsTranslationController({
    comments,
    langKey: systemLang,
    getTranslationFromComment,
    translateMissing: translateMissing as any,
    translateStatus: { pending, error },
  });

  return (
    <CommentPopoverContentView
      comments={comments}
      platform={platform}
      needTranslate={needTranslate}
      displayTranslation={displayTranslation}
      translations={translations}
      translating={translating}
      translationError={translationError}
      onToggleTranslation={toggleTranslation}
    />
  );
};

export const OrderComment = (props: OrderCommentProps) => {
  const { platform, comments } = props;

  const [visible, setVisible] = useState(false);

  const { ratingValue } = useCommentRatingValue(comments, platform);

  return (
    <Popover
      visible={visible}
      onVisibleChange={setVisible}
      trigger="click"
      content={visible ? <CommentContent {...props} /> : null}
      getPopupContainer={getPopupContainer}
    >
      <div className="flex items-center gap-2  cursor-pointer text-[12px]">
        <Rate
          className="!text-[14px] !text-[#FFC758]"
          disabled
          allowHalf
          count={5}
          value={ratingValue}
        />
        <DownOutlined className="!text-[#84868C]" />
      </div>
    </Popover>
  );
};
